import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Spinner,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { UsersThree, ChartPie, TrendUp, WarningCircle, MagnifyingGlass } from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, pct, qtd } from '../format';
import { IAuditoriaEmpresa, IClientesFornecedores, CliForTipo } from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { EmptyState } from '../components/EmptyState';
import { UI } from '../ui';

interface ClientesFornScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Drill: abre Notas Fiscais filtrado pelo parceiro clicado. */
  onOpenNotas: (busca: string, tipo?: 'saida' | 'entrada' | 'servico') => void;
}

export function ClientesFornScreen({ empresa, periodo, onOpenNotas }: ClientesFornScreenProps) {
  const { getClientesFornecedores } = useAuditoriaInteligente();
  const [tipo, setTipo] = useState<CliForTipo>('clientes');
  const [busca, setBusca] = useState('');

  const ehCliente = tipo === 'clientes';
  const rotulo = ehCliente ? 'Cliente' : 'Fornecedor';
  const valLabel = ehCliente ? 'Faturamento' : 'Compras';
  const tipoNota = ehCliente ? ('saida' as const) : ('entrada' as const);

  const { data, isFetching } = useQuery<IClientesFornecedores>({
    queryKey: ['ai-clientes-fornecedores', { id: empresa?.id, tipo, ...periodo }],
    queryFn: getClientesFornecedores,
    enabled: !!empresa,
  });

  const porUf = useMemo(
    () => [...(data?.por_uf ?? [])].sort((a, b) => b.total - a.total).slice(0, 8),
    [data],
  );
  const maxUf = porUf.length ? Math.max(...porUf.map(u => u.total)) : 0;

  // Busca alcança todo o ranking (não só o Top 10); sem busca, mostra o Top 10.
  const rankingView = useMemo(() => {
    const todos = data?.ranking ?? [];
    const q = busca.trim().toLowerCase();
    if (!q) return todos.slice(0, 10);
    return todos.filter(c => `${c.nome} ${c.cnpj_cpf}`.toLowerCase().includes(q)).slice(0, 50);
  }, [data, busca]);

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver clientes e fornecedores."
        icon={UsersThree}
      />
    );
  }

  const conc = data?.concentracao;
  const tabs: { value: CliForTipo; label: string }[] = [
    { value: 'clientes', label: 'Clientes' },
    { value: 'fornecedores', label: 'Fornecedores' },
  ];

  return (
    <Box>
      <Flex gap={1} borderBottom="1px solid #E7ECF2" mb={5} wrap="wrap">
        {tabs.map(t => {
          const isActive = t.value === tipo;
          return (
            <Flex
              key={t.value}
              align="center"
              px={4}
              py={3}
              cursor="pointer"
              onClick={() => setTipo(t.value)}
              borderBottom="2.5px solid"
              borderColor={isActive ? UI.brand : 'transparent'}
              color={isActive ? UI.brand : UI.muted}
              mb="-1px"
              _hover={{ color: isActive ? UI.brand : UI.ink }}
            >
              <Text fontSize="13px" fontWeight={700}>
                {t.label}
              </Text>
            </Flex>
          );
        })}
      </Flex>

      {isFetching && !data ? (
        <Box textAlign="center" py={16}>
          <Spinner />
        </Box>
      ) : (
        <Box>
          <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={5}>
            <KpiCard
              label={ehCliente ? 'Clientes ativos' : 'Fornecedores ativos'}
              value={(data?.ranking ?? []).length}
              icon={UsersThree}
              iconColor={UI.brand}
              iconBg={UI.infoSoft}
              info={
                <InfoBloco
                  oque={
                    ehCliente
                      ? 'Quantidade de clientes distintos que tiveram notas de saída emitidas no período.'
                      : 'Quantidade de fornecedores distintos que tiveram notas de entrada no período.'
                  }
                  dados={
                    ehCliente
                      ? 'extract_notas_saida (cliente_id, data_saida) × extract_clientes'
                      : 'extract_notas_entrada (fornecedor_id, data_entrada) × extract_fornecedores'
                  }
                  filtro={FILTRO.empresa}
                />
              }
            />
            <KpiCard
              label="Concentração Top 1"
              value={pct(conc?.pct_top1)}
              sub={(conc?.pct_top1 ?? 0) > 30 ? 'Risco alto' : 'Saudável'}
              icon={TrendUp}
              iconColor={UI.warn}
              iconBg={UI.warnSoft}
              info={
                <InfoBloco
                  oque={`Quanto o maior ${rotulo.toLowerCase()} representa do ${valLabel.toLowerCase()} total do período. Acima de 30% indica dependência concentrada.`}
                  dados={
                    ehCliente
                      ? 'extract_notas_saida.valor agrupado por cliente_nome ÷ total de saídas'
                      : 'extract_notas_entrada.valor agrupado por fornecedor_nome ÷ total de entradas'
                  }
                  filtro={FILTRO.empresa}
                />
              }
            />
            <KpiCard
              label="Concentração Top 5"
              value={pct(conc?.pct_top5)}
              sub={`Top 10: ${pct(conc?.pct_top10)}`}
              icon={ChartPie}
              iconColor="#8B5CF6"
              iconBg="#F0EAFB"
              info={
                <InfoBloco
                  oque={`Participação dos 5 maiores ${ehCliente ? 'clientes' : 'fornecedores'} no ${valLabel.toLowerCase()} total do período (e dos 10 maiores no rodapé).`}
                  dados={
                    ehCliente
                      ? 'extract_notas_saida.valor por cliente_nome (ranking Top 5/10 ÷ total)'
                      : 'extract_notas_entrada.valor por fornecedor_nome (ranking Top 5/10 ÷ total)'
                  }
                  filtro={FILTRO.empresa}
                />
              }
            />
            <KpiCard
              label="Cadastro incompleto"
              value={(data?.sem_cnpj ?? []).length}
              sub="Sem CNPJ"
              icon={WarningCircle}
              iconColor={UI.neg}
              iconBg={UI.negSoft}
              info={
                <InfoBloco
                  oque={`${ehCliente ? 'Clientes' : 'Fornecedores'} cadastrados sem CNPJ/CPF válido (em branco ou com menos de 11 dígitos), o que prejudica a auditoria fiscal.`}
                  dados={
                    ehCliente
                      ? 'extract_clientes.cnpj_cpf (nulo ou < 11 dígitos)'
                      : 'extract_fornecedores.cnpj_cpf (nulo ou < 11 dígitos)'
                  }
                  filtro={FILTRO.empresaSemPeriodo}
                />
              }
            />
          </Grid>

          <Grid templateColumns={{ base: '1fr', lg: '3fr 2fr' }} gap={5} mb={5}>
            <Box
              bg="white"
              border="1px solid"
              borderColor={UI.border}
              borderRadius="16px"
              overflow="hidden"
            >
              <Flex px={5} pt={4} pb={3} justify="space-between" align="center" gap={3} wrap="wrap">
                <Text fontSize="13px" fontWeight={700} color={UI.ink}>
                  {busca
                    ? `${rankingView.length} ${ehCliente ? 'clientes' : 'fornecedores'}`
                    : `Top 10 ${ehCliente ? 'clientes por faturamento' : 'fornecedores por compras'}`}
                </Text>
                <InputGroup size="sm" w={{ base: '100%', md: '220px' }}>
                  <InputLeftElement pointerEvents="none" color={UI.muted}>
                    <MagnifyingGlass size={15} />
                  </InputLeftElement>
                  <Input
                    placeholder="Nome ou CNPJ/CPF…"
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    borderRadius="10px"
                    bg="white"
                  />
                </InputGroup>
              </Flex>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={UI.rowHover}>
                    <Tr>
                      <Th>{rotulo}</Th>
                      <Th>UF</Th>
                      <Th isNumeric>Notas</Th>
                      <Th isNumeric>{valLabel}</Th>
                      <Th isNumeric>Ticket</Th>
                      <Th isNumeric>% total</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rankingView.map(c => (
                      <Tr
                        key={`${c.cnpj_cpf}-${c.nome}`}
                        cursor="pointer"
                        _hover={{ bg: UI.rowHover }}
                        onClick={() => onOpenNotas(c.cnpj_cpf || c.nome, tipoNota)}
                        title={`Ver as notas de ${c.nome}`}
                      >
                        <Td maxW="240px" whiteSpace="normal">
                          <Text fontWeight={700} fontSize="12.5px">
                            {c.nome}
                          </Text>
                          <Text fontSize="11px" color={UI.muted}>
                            {c.cnpj_cpf}
                          </Text>
                        </Td>
                        <Td>{c.uf}</Td>
                        <Td isNumeric>{qtd(c.qtd_notas)}</Td>
                        <Td isNumeric fontWeight={700}>
                          {formatCurrency(num(c.total))}
                        </Td>
                        <Td isNumeric color={UI.muted}>
                          {num(c.qtd_notas) ? formatCurrency(num(c.total) / num(c.qtd_notas)) : '—'}
                        </Td>
                        <Td isNumeric>{pct(c.pct_total)}</Td>
                      </Tr>
                    ))}
                    {rankingView.length === 0 && (
                      <Tr>
                        <Td colSpan={6} textAlign="center" py={6} color={UI.muted}>
                          {busca ? 'Nenhum parceiro com esse filtro.' : 'Sem parceiros no período.'}
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            <Box
              bg={UI.glass}
              border="1px solid"
              borderColor={UI.glassBorder}
              borderRadius="16px"
              backdropFilter={UI.glassBlur}
              boxShadow={UI.glassShadow}
              p={5}
            >
              <Text fontSize="13px" fontWeight={700} mb={4} color={UI.ink}>
                Distribuição por UF
              </Text>
              {porUf.length === 0 && (
                <Text fontSize="13px" color={UI.muted}>
                  Sem dados de UF.
                </Text>
              )}
              {porUf.map(u => (
                <Flex key={u.uf} align="center" gap={3} mb={3}>
                  <Text w="30px" fontWeight={800} fontSize="12px" color={UI.muted}>
                    {u.uf}
                  </Text>
                  <Box flex={1} h="7px" borderRadius="6px" bg={UI.border} overflow="hidden">
                    <Box
                      h="100%"
                      borderRadius="6px"
                      bg="#1AA6B7"
                      w={`${maxUf ? (u.total / maxUf) * 100 : 0}%`}
                    />
                  </Box>
                  <Text w="90px" textAlign="right" fontWeight={700} fontSize="12.5px">
                    {formatCurrency(u.total)}
                  </Text>
                </Flex>
              ))}
            </Box>
          </Grid>

          {(data?.sem_cnpj ?? []).length > 0 && (
            <Box
              bg="white"
              border="1px solid"
              borderColor={UI.border}
              borderRadius="16px"
              overflow="hidden"
              mb={5}
            >
              <Box px={5} pt={4} pb={2}>
                <Text fontSize="13px" fontWeight={700} color={UI.ink}>
                  ⚠ Cadastro incompleto (sem CNPJ)
                </Text>
              </Box>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={UI.rowHover}>
                    <Tr>
                      <Th>{rotulo}</Th>
                      <Th>UF</Th>
                      <Th>Cidade</Th>
                      <Th isNumeric>Notas</Th>
                      <Th isNumeric>{valLabel}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(data?.sem_cnpj ?? []).map(x => (
                      <Tr
                        key={`${x.nome}-${x.cidade}-${x.qtd_notas}`}
                        cursor="pointer"
                        _hover={{ bg: UI.rowHover }}
                        onClick={() => onOpenNotas(x.nome, tipoNota)}
                        title={`Ver as notas de ${x.nome}`}
                      >
                        <Td fontWeight={700}>{x.nome}</Td>
                        <Td>{x.uf}</Td>
                        <Td>{x.cidade}</Td>
                        <Td isNumeric>{qtd(x.qtd_notas)}</Td>
                        <Td isNumeric>{formatCurrency(x.total)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {ehCliente && (data?.circular ?? []).length > 0 && (
            <Flex
              align="center"
              gap={3}
              p={4}
              borderRadius="14px"
              bg="#FFFBF2"
              border="1px solid #F4E4C4"
            >
              <Flex
                align="center"
                justify="center"
                w="38px"
                h="38px"
                borderRadius="11px"
                bg={UI.warnSoft}
                color={UI.warn}
                flexShrink={0}
              >
                <WarningCircle size={20} />
              </Flex>
              <Box>
                <Text fontWeight={700} color={UI.ink}>
                  {(data?.circular ?? []).length} CNPJ aparecem como cliente E fornecedor
                </Text>
                <Text fontSize="12.5px" color={UI.muted}>
                  Operação circular / intercompany —{' '}
                  {(data?.circular ?? [])
                    .map(c => c.nome_cliente)
                    .slice(0, 5)
                    .join(', ')}
                  . Pode merecer atenção fiscal.
                </Text>
              </Box>
              <Badge
                ml="auto"
                style={{ backgroundColor: UI.warnSoft, color: UI.warn }}
                borderRadius="full"
                px={2.5}
                py={1}
                fontSize="11.5px"
                fontWeight={700}
                textTransform="none"
              >
                {(data?.circular ?? []).length} casos
              </Badge>
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
}
